import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import AlertModal from '../components/modals/AlertModal';
import CornerNotice from '../components/notifications/CornerNotice';
import { FormEditor } from './FormCreator';
import { FormAssignmentsPanel } from './FormAssignments';
import { useAuth } from '../contexts/AuthContext';
import { usePageConfig } from '../contexts/PageConfigContext';
import { getFormsPageCapabilities } from '../utils/formsPage';
import { FORM_TYPE_LABELS, normalizeLegacyFormType } from '../constants/formScoring';
import { isPointsForm } from '../utils/formScoring';
import './FormsManager.css';

const PROMPT_ALERT = {
  presentation: 'prompt',
  overlayOpacity: 0.16,
  overlayColor: '15, 23, 42',
  zIndex: 2000
};

const SUCCESS_NOTICE = {
  position: 'bottom-left',
  containerColor: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
  textColor: '#14532d',
  showOverlay: false,
  dismissOnClick: true,
  autoClose: true,
  viewTime: 4500
};

const FormsPlusIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const FormsEditIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const FormsLinkIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const FormsDeleteIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const FormsManager = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'assignments' ? 'assignments' : 'library';
  const editId = searchParams.get('edit');
  const { fetchWithAuth } = useAuth();
  const pageConfig = usePageConfig();
  const formsCaps = useMemo(
    () => getFormsPageCapabilities(pageConfig?.configuration),
    [pageConfig?.configuration]
  );

  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alertModal, setAlertModal] = useState(null);

  const showFormAlert = (subject, body, variant = 'error', details = '') => {
    setAlertModal({
      variant,
      subject,
      body,
      details: details || undefined,
      actions: [{ label: 'OK', onClick: () => setAlertModal(null), variant: 'primary' }]
    });
  };
  const [deletingFormId, setDeletingFormId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [cornerNotice, setCornerNotice] = useState(null);

  const showNotice = useCallback((payload) => {
    setCornerNotice({
      isOpen: true,
      ...SUCCESS_NOTICE,
      ...payload
    });
  }, []);

  const openEditor = (id, { focus } = {}) => {
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    next.set('edit', id === 'new' ? 'new' : String(id));
    if (focus) next.set('focus', focus);
    else next.delete('focus');
    setSearchParams(next, { replace: true });
  };

  const closeEditor = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    next.delete('focus');
    setSearchParams(next, { replace: true });
  };

  const loadForms = async () => {
    setIsLoading(true);
    try {
      const response = await fetchWithAuth('/api/forms');
      const data = await response.json();

      if (data.success) {
        setForms(data.data || []);
      } else {
        throw new Error(data.message || 'Failed to load forms');
      }
    } catch (error) {
      showFormAlert('Load failed', 'Failed to load forms', 'error', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, []);

  const requestDelete = (formId, formTitle, e) => {
    e?.stopPropagation();
    setDeleteConfirm({ id: formId, title: formTitle });
  };

  const handleDelete = async () => {
    if (!deleteConfirm || deletingFormId) return;
    const { id: formId, title: formTitle } = deleteConfirm;
    setDeletingFormId(formId);
    try {
      const response = await fetchWithAuth(`/api/forms/${formId}`, { method: 'DELETE' });
      const data = await response.json();

      if (data.success) {
        setDeleteConfirm(null);
        if (editId && String(editId) === String(formId)) {
          closeEditor();
        }
        showNotice({ title: 'Deleted', text: `"${formTitle}" was removed.` });
        await loadForms();
      } else {
        throw new Error(data.message || 'Delete failed');
      }
    } catch (error) {
      setDeleteConfirm(null);
      showFormAlert('Delete failed', 'Failed to delete form', 'error', error.message);
    } finally {
      setDeletingFormId(null);
    }
  };

  const handleEditorSaved = async (newId) => {
    await loadForms();
    openEditor(newId);
  };

  const handleSelectFormFromAssignments = (formId) => {
    openEditor(formId);
  };

  const renderLibraryList = () => (
    <div className="forms-list">
      <div className="forms-list-head">
        {forms.length > 0 && !isLoading ? (
          <span className="forms-count">
            {forms.length} form{forms.length !== 1 ? 's' : ''}
          </span>
        ) : (
          <span className="forms-count">No forms yet</span>
        )}
        <button
          type="button"
          onClick={() => openEditor('new')}
          className="forms-list-add"
          title="New form"
          aria-label="New form"
          disabled={!formsCaps.canCreate}
          style={!formsCaps.canCreate ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
        >
          <FormsPlusIcon />
        </button>
      </div>

      {forms.length > 0 ? (
        <ul className="forms-list-rows">
          {forms.map((form) => {
            const questions =
              typeof form.questions === 'string' ? JSON.parse(form.questions) : form.questions || [];
            const formType = normalizeLegacyFormType(form.type);
            const typeLabel = FORM_TYPE_LABELS[formType] || formType;
            const totalPoints = isPointsForm(formType)
              ? questions.reduce((sum, q) => sum + (Number(q.points) || 0), 0)
              : null;

            return (
              <li key={form.id} className="forms-list-row">
                <div className="forms-list-row-main">
                  <span className="forms-list-row-title">{form.title}</span>
                  <span className="forms-list-row-meta">
                    {questions.length} question{questions.length !== 1 ? 's' : ''}
                    {' · '}{typeLabel}
                    {totalPoints != null ? ` · ${totalPoints} pts` : ''}
                    {form.primary_taxonomy_name ? ` · ${form.primary_taxonomy_name}` : ''}
                  </span>
                </div>
                <div className="forms-list-row-actions">
                  <button
                    type="button"
                    onClick={() => openEditor(form.id)}
                    className="forms-list-icon-btn"
                    title="Edit form"
                    aria-label={`Edit ${form.title}`}
                    disabled={!formsCaps.canEdit}
                    style={!formsCaps.canEdit ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                  >
                    <FormsEditIcon />
                  </button>
                  <button
                    type="button"
                    onClick={() => openEditor(form.id, { focus: 'assignments' })}
                    className="forms-list-icon-btn"
                    title="Assignments"
                    aria-label={`Assignments for ${form.title}`}
                    disabled={!formsCaps.canAssign}
                    style={!formsCaps.canAssign ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                  >
                    <FormsLinkIcon />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => requestDelete(form.id, form.title, e)}
                    disabled={deletingFormId === form.id || !formsCaps.canDelete}
                    className="forms-list-icon-btn forms-list-icon-btn--danger"
                    title="Delete form"
                    aria-label={`Delete ${form.title}`}
                    style={!formsCaps.canDelete ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                  >
                    <FormsDeleteIcon />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );

  return (
    <div className="forms-page">
      <LoadingOverlay isVisible={isLoading && !editId} message="Loading forms…" />

      {editId ? (
        <div className="forms-builder">
          <FormEditor
            key={`${editId}-${searchParams.get('focus') || ''}`}
            formId={editId}
            focus={searchParams.get('focus')}
            formsCaps={formsCaps}
            fetchWithAuth={fetchWithAuth}
            onBack={closeEditor}
            onSaved={handleEditorSaved}
            onNotice={showNotice}
          />
        </div>
      ) : tab === 'assignments' ? (
        <>
          <header className="forms-top">
            <div className="forms-top-main">
              <h1 className="forms-title">Assignments</h1>
              <p className="forms-subtitle">Forms linked to group-periods.</p>
            </div>
          </header>
          <FormAssignmentsPanel
            onSelectForm={handleSelectFormFromAssignments}
            onNotice={showNotice}
          />
        </>
      ) : (
        <>
          <header className="forms-top">
            <div className="forms-top-main">
              <h1 className="forms-title">Forms</h1>
              <p className="forms-subtitle">
                Build questionnaires and assign them to group-periods. Profile visibility uses the Custom Forms app.
              </p>
            </div>
          </header>
          {renderLibraryList()}
        </>
      )}

      {alertModal ? (
        <AlertModal
          isOpen={true}
          onClose={() => setAlertModal(null)}
          showCloseButton={false}
          allowOverlayClose={true}
          {...PROMPT_ALERT}
          variant={alertModal.variant || 'info'}
          subject={alertModal.subject}
          body={alertModal.body}
          details={alertModal.details}
          actions={alertModal.actions}
        />
      ) : null}

      {deleteConfirm ? (
        <AlertModal
          isOpen={true}
          onClose={() => !deletingFormId && setDeleteConfirm(null)}
          variant="warning"
          subject="Delete form?"
          body={`"${deleteConfirm.title}" will be permanently removed.`}
          showCloseButton={false}
          allowOverlayClose={!deletingFormId}
          actions={[
            { label: 'Cancel', onClick: () => setDeleteConfirm(null), variant: 'secondary' },
            {
              label: deletingFormId ? 'Deleting…' : 'Delete',
              onClick: handleDelete,
              variant: 'primary',
              style: { background: '#dc2626' }
            }
          ]}
          {...PROMPT_ALERT}
        />
      ) : null}

      {cornerNotice ? (
        <CornerNotice {...cornerNotice} onClose={() => setCornerNotice(null)} />
      ) : null}
    </div>
  );
};

export default FormsManager;
